import { getContext } from 'configs/context';

import { BATCH_MINIAPP_ID } from 'pages/Batch/configs';

import { MAGIC_FLOW_WEBHOOK_URL, SUBMIT_WEB_HOOK_URL, WEBHOOK_FOLDER_NAME } from 'configs/constants';

interface SubmitWebhookParams {
  webhookId: string;
  params: Record<string, unknown>;
}

type PollStatus = 'COMPLETED' | 'FAILED' | 'PENDING' | 'PROCESSING';

interface PollResponse {
  response: {
    status: PollStatus;
    output?: { url: string };
    result?: Array<{
      type: string;
      result: {
        url: string;
        metadata?: { width: number; height: number };
      };
    }>;
  };
}

export interface PollResult {
  url: string;
  metadata?: { width: number; height: number };
}

export async function submitWebhook({ webhookId, params }: SubmitWebhookParams): Promise<{ id: string }> {
  const response = await getContext().utils.api.fetch(SUBMIT_WEB_HOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      params: {
        webhookId,
        params,
        driveOptions: {
          packageId: BATCH_MINIAPP_ID,
          folderName: WEBHOOK_FOLDER_NAME,
        },
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Webhook submission failed: ${response.status}`);
  }

  const data = await response.json();
  return { id: data.response.id };
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const pollRequest = async (url: string): Promise<PollResponse> => {
  const response = await getContext().utils.api.fetch(url, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.json();
};

export async function pollUntilCompleted(id: string, signal?: AbortSignal): Promise<PollResult> {
  const url = `${MAGIC_FLOW_WEBHOOK_URL}/${id}/result`;
  const initialInterval = 1000;
  const maxInterval = 8000;
  const backoffFactor = 1.5;
  const maxAttempts = 150;

  let attempts = 0;
  let currentInterval = initialInterval;

  while (attempts < maxAttempts) {
    if (signal?.aborted) {
      throw new Error('Generation cancelled');
    }

    // eslint-disable-next-line no-await-in-loop
    await sleep(currentInterval);

    // eslint-disable-next-line no-await-in-loop
    const result = await pollRequest(url);
    attempts++;

    if (result.response.status === 'COMPLETED') {
      const firstResult = result.response.result?.[0]?.result;
      const resultUrl = firstResult?.url ?? result.response.output?.url;
      if (!resultUrl) {
        throw new Error('Generation completed but no result URL found');
      }
      return { url: resultUrl, metadata: firstResult?.metadata };
    }

    if (result.response.status === 'FAILED') {
      throw new Error('Generation failed');
    }

    currentInterval = Math.min(currentInterval * backoffFactor, maxInterval);
  }

  throw new Error('Generation timed out');
}
